import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogOut, LifeBuoy, Building2, LayoutDashboard, Users, Wallet, TrendingUp, History, SlidersHorizontal, Search, ArrowUpDown, ChevronUp, ChevronDown, Sun, Moon, Mail, Tag, Menu, X, Megaphone, MessageSquare, FileText, Globe } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import Personalisation from "@/components/valhalla/Personalisation";
import Overview from "@/components/valhalla/OverviewTab";
import OrgRow from "@/components/valhalla/OrgRow";
import OrgDetail from "@/components/valhalla/OrgDetail";
import DeleteOrgDialog from "@/components/valhalla/DeleteOrgDialog";
import MaintenanceControl from "@/components/valhalla/MaintenanceControl";
import PlatformSettings from "@/components/valhalla/PlatformSettings";
import SubscriptionsView from "@/components/valhalla/SubscriptionsView";
import AccountingView from "@/components/valhalla/AccountingView";
import PermissionsView from "@/components/valhalla/PermissionsView";
import OffersView from "@/components/valhalla/OffersView";
import FeedbackView from "@/components/valhalla/FeedbackView";
import NewsView from "@/components/valhalla/NewsView";
import FacturationView from "@/components/valhalla/FacturationView";
import SiteView from "@/components/valhalla/SiteView";

function Dashboard({ token, onLogout, perms }) {
  const { theme, setTheme } = useTheme();
  const can = (section, level = "read") => {
    if (!perms) return true;
    const v = perms[section];
    return level === "write" ? v === "write" : v === "read" || v === "write";
  };
  const [tab, setTab] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [fin, setFin] = useState(null);
  const [detail, setDetail] = useState(null);
  const [delOrg, setDelOrg] = useState(null);
  const [plans, setPlans] = useState([]);
  const saversRef = useRef({});
  const [savingAll, setSavingAll] = useState(false);
  const saveAll = async () => {
    setSavingAll(true);
    let n = 0;
    for (const fn of Object.values(saversRef.current)) { try { const changed = await fn(); if (changed) n++; } catch { /* row error already toasted */ } }
    setSavingAll(false);
    toast.success(n > 0 ? `${n} prestataire(s) mis à jour` : "Aucune modification à enregistrer");
    loadOrgs();
  };
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const loadOrgs = () => axios.get(`${API}/superadmin/orgs`, h).then(({ data }) => setOrgs(data)).catch(() => {});
  const loadTickets = () => axios.get(`${API}/superadmin/support`, h).then(({ data }) => setTickets(data)).catch(() => {});
  const loadStats = () => axios.get(`${API}/superadmin/stats`, h).then(({ data }) => setStats(data)).catch(() => {});
  const loadHistory = () => axios.get(`${API}/superadmin/maintenance-history`, h).then(({ data }) => setHistory(data)).catch(() => {});
  const loadFin = () => axios.get(`${API}/superadmin/finance`, h).then(({ data }) => setFin(data)).catch(() => {});
  useEffect(() => { loadOrgs(); loadTickets(); loadStats(); loadHistory(); loadFin(); axios.get(`${API}/superadmin/plans`, h).then(({ data }) => setPlans(data)).catch(() => {}); }, []); // eslint-disable-line
  const resolve = async (id) => { await axios.post(`${API}/superadmin/support/${id}/resolve`, {}, h); loadTickets(); };
  const openCount = tickets.filter((t) => t.status !== "resolved").length;
  const [orgSearch, setOrgSearch] = useState("");
  const [orgSort, setOrgSort] = useState({ key: "name", dir: "asc" });
  const toggleSort = (key) => setOrgSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" });
  const q = orgSearch.trim().toLowerCase();
  const visibleOrgs = orgs
    .filter((o) => !q || (o.name || "").toLowerCase().includes(q) || (o.admin_email || "").toLowerCase().includes(q))
    .sort((a, b) => {
      const { key, dir } = orgSort; const m = dir === "asc" ? 1 : -1;
      if (key === "revenue") return m * ((a.revenue || 0) - (b.revenue || 0));
      if (key === "last_activity") return m * ((a.last_seen ? Date.parse(a.last_seen) : 0) - (b.last_seen ? Date.parse(b.last_seen) : 0));
      if (key === "plan") return m * (a.plan || "").localeCompare(b.plan || "", "fr");
      if (key === "subscription_status") return m * (a.subscription_status || "").localeCompare(b.subscription_status || "");
      return m * (a.name || "").localeCompare(b.name || "", "fr");
    });
  const SortHead = ({ k, label, cls = "px-2 py-3" }) => (
    <th className={cls}>
      <button data-testid={`sa-sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 uppercase transition-colors hover:text-white">
        {label}
        {orgSort.key === k ? (orgSort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-[#FF6A00]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#FF6A00]" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
      </button>
    </th>
  );
  const NAV = [
    { k: "overview", l: "Tableau de bord", icon: LayoutDashboard },
    { k: "orgs", l: `Prestataires (${orgs.length})`, icon: Building2 },
    { k: "subscriptions", l: "Abonnements", icon: TrendingUp },
    { k: "facturation", l: "Facturation", icon: FileText },
    { k: "accounting", l: "Comptabilité", icon: Wallet },
    { k: "support", l: "Support", icon: LifeBuoy, badge: openCount },
    { k: "news", l: "Annonces", icon: Megaphone },
    { k: "feedback", l: "Feedback", icon: MessageSquare },
    { k: "maintenance", l: "Maintenances", icon: History },
    { k: "settings", l: "Paramètres", icon: SlidersHorizontal },
    { k: "offers", l: "Offres", icon: Tag },
    { k: "site", l: "Site vitrine", icon: Globe },
    { k: "personalisation", l: "Personnalisation", icon: Mail },
    { k: "permissions", l: "Permissions & Utilisateurs", icon: Users },
  ];
  useEffect(() => { if (!can(tab)) { const f = NAV.find((n) => can(n.k)); if (f) setTab(f.k); } }, [perms]); // eslint-disable-line
  const maintMap = {
    scheduled: { l: "Programmée", c: "bg-amber-500/15 text-amber-400" },
    active: { l: "En cours", c: "bg-red-500/15 text-red-400" },
    ended: { l: "Terminée", c: "bg-zinc-700/40 text-zinc-300" },
    cancelled: { l: "Annulée", c: "bg-zinc-700/40 text-zinc-400" },
  };
  const fmt = (d) => new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="flex min-h-screen bg-[#09090b] text-white">
      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-4 py-3 md:hidden">
        <div className="flex items-center gap-2"><img src="/halgaros-icon-128.png" alt="" className="h-7 w-7 rounded-lg" /><span className="font-display text-base font-bold">Valhalla</span></div>
        <button data-testid="sa-mobile-menu" onClick={() => setNavOpen(true)} className="rounded-lg p-2 text-zinc-300 hover:bg-zinc-800"><Menu className="h-5 w-5" /></button>
      </div>
      {/* Mobile overlay */}
      {navOpen && <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setNavOpen(false)} />}
      <aside className={`fixed left-0 top-0 z-50 flex h-screen w-60 flex-col border-r border-zinc-800 bg-zinc-950 p-4 transition-transform duration-200 md:z-30 md:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="mb-6 flex items-center justify-between px-2">
          <div className="flex items-center gap-2"><img src="/halgaros-icon-128.png" alt="" className="h-8 w-8 rounded-lg" /><span className="font-display text-lg font-bold">Valhalla</span></div>
          <button data-testid="sa-nav-close" onClick={() => setNavOpen(false)} className="rounded-lg p-1 text-zinc-400 hover:text-white md:hidden"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {NAV.filter((n) => can(n.k)).map((n) => (
            <button key={n.k} data-testid={`sa-nav-${n.k}`} onClick={() => { setTab(n.k); setNavOpen(false); }} className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${tab === n.k ? "bg-[#FF6A00]/12 text-[#FF6A00]" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"}`}>
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.6} />{n.l}
              {n.badge > 0 && <span className="ml-auto rounded-full bg-[#FF6A00] px-1.5 text-xs text-white">{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="border-t border-zinc-800 pt-3">
          {can("maintenance", "write") && <MaintenanceControl token={token} />}
          <button data-testid="sa-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="mt-2 flex w-full items-center gap-2 rounded-xl px-3.5 py-2 text-sm text-zinc-400 hover:text-white">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}{theme === "dark" ? "Thème clair" : "Thème sombre"}
          </button>
          <button data-testid="sa-logout" onClick={onLogout} className="mt-2 flex w-full items-center gap-2 rounded-xl px-3.5 py-2 text-sm text-zinc-400 hover:text-red-400"><LogOut className="h-4 w-4" />Déconnexion</button>
        </div>
      </aside>
      <main className="flex-1 pt-14 md:pl-60 md:pt-0">
        <div className="p-4 md:p-6">
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "subscriptions" && <SubscriptionsView fin={fin} />}
          {tab === "facturation" && <FacturationView token={token} />}
          {tab === "accounting" && <AccountingView fin={fin} token={token} />}
          {tab === "settings" && <PlatformSettings token={token} readOnly={!can("settings", "write")} />}
          {tab === "offers" && <OffersView token={token} readOnly={!can("offers", "write")} />}
          {tab === "site" && <SiteView token={token} readOnly={!can("site", "write")} />}
          {tab === "personalisation" && <Personalisation token={token} readOnly={!can("personalisation", "write")} />}
          {tab === "news" && <NewsView token={token} />}
          {tab === "feedback" && <FeedbackView token={token} />}
          {tab === "permissions" && <PermissionsView token={token} readOnly={!can("permissions", "write")} />}

          {tab === "orgs" && detail && <OrgDetail oid={detail} token={token} onBack={() => setDetail(null)} onDelete={setDelOrg} />}
          {tab === "orgs" && !detail && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="sa-org-search" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="Rechercher un prestataire (nom ou email)…" className="h-10 border-zinc-800 bg-zinc-900 pl-9 text-sm" />
                </div>
                <span className="text-xs text-zinc-500" data-testid="sa-org-count">{visibleOrgs.length} / {orgs.length} prestataires</span>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-zinc-800 bg-zinc-950">
                <table className="w-full text-left">
                  <thead className="border-b border-zinc-800 text-xs uppercase text-zinc-500">
                    <tr>
                      <SortHead k="name" label="Prestataire" cls="px-3 py-3" />
                      <SortHead k="revenue" label="CA confirmé" cls="px-3 py-3" />
                      <SortHead k="last_activity" label="Dernière activité" cls="px-3 py-3" />
                      <SortHead k="plan" label="Plan" />
                      <th className="px-2 py-3">Prix perso (€)</th><th className="px-2 py-3">Remise (%)</th><th className="px-2 py-3">Commission (%)</th>
                      <SortHead k="subscription_status" label="Abonnement" />
                      <th className="px-2 py-3">Connect</th><th className="px-2 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrgs.length === 0
                      ? <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-zinc-500" data-testid="sa-org-empty">Aucun prestataire ne correspond à la recherche.</td></tr>
                      : visibleOrgs.map((o) => <OrgRow key={o.id} o={o} token={token} onSaved={loadOrgs} onOpen={setDetail} onDelete={setDelOrg} plans={plans} registerSaver={(id, fn) => { saversRef.current[id] = fn; }} />)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-zinc-500 sm:flex-1">Modifiez les lignes puis enregistrez tout d'un coup. Changer le prix facturé (prix perso, plan ou remise) crée un nouvel abonnement Revolut et envoie au prestataire un email pour renseigner sa carte.</p>
                <Button data-testid="sa-save-all" onClick={saveAll} disabled={savingAll} className="shrink-0 whitespace-nowrap rounded-full bg-[#FF6A00] px-6 font-semibold text-white hover:bg-[#E65F00]">{savingAll ? "Enregistrement…" : "Enregistrer les modifications"}</Button>
              </div>
            </div>
          )}

          {tab === "support" && (
            <div className="space-y-3" data-testid="sa-support-list">
              {tickets.length === 0 && <p className="text-zinc-500">Aucune demande de support.</p>}
              {tickets.map((t) => (
                <div key={t.id} data-testid={`sa-ticket-${t.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-white">{t.subject}</p>
                      <p className="text-xs text-zinc-500">{t.org_name || "?"} · {t.user_email}</p>
                    </div>
                    {t.status === "resolved"
                      ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-400">Traité</span>
                      : <Button data-testid={`sa-resolve-${t.id}`} onClick={() => resolve(t.id)} className="h-8 rounded-full bg-[#FF6A00] px-3 text-xs hover:bg-[#E65F00]">Marquer traité</Button>}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">{t.message}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "maintenance" && (
            <div className="space-y-3" data-testid="sa-maintenance-history">
              {history.length === 0 && <p className="text-zinc-500">Aucune maintenance enregistrée.</p>}
              {history.map((m) => {
                const s = maintMap[m.status] || maintMap.ended;
                return (
                  <div key={m.id} data-testid={`sa-maint-${m.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-white">{m.mode === "scheduled" ? "Programmée" : "Immédiate"} · {fmt(m.started_at)}</p>
                        <p className="text-xs text-zinc-500">Durée {m.duration_minutes ? `${m.duration_minutes} min` : "—"}{m.ended_at ? ` · terminée le ${fmt(m.ended_at)}` : ""}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${s.c}`}>{s.l}</span>
                    </div>
                    {m.message && <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">{m.message}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <DeleteOrgDialog org={delOrg} token={token} onClose={() => setDelOrg(null)} onDeleted={() => { loadOrgs(); loadStats(); setDetail(null); }} />
    </div>
  );
}

export default function Valhalla() {
  const [token, setToken] = useState(() => localStorage.getItem("halgaros_sa_token") || "");
  const [saUser, setSaUser] = useState(() => { try { return JSON.parse(localStorage.getItem("halgaros_sa_user") || "null"); } catch { return null; } });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const finishLogin = (data) => {
    if (!data.user?.is_platform_admin) { toast.error("Ce compte n'a pas accès à Valhalla"); return; }
    localStorage.setItem("halgaros_sa_token", data.token);
    localStorage.setItem("halgaros_sa_user", JSON.stringify(data.user));
    setSaUser(data.user); setToken(data.token); setChallenge(null); setCode("");
  };
  const login = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.two_factor_required) { setChallenge(data.challenge); setBusy(false); return; }
      finishLogin(data);
    } catch { toast.error("Identifiants incorrects"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/auth/2fa/verify`, { challenge, code: code.trim() }); finishLogin(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };
  const logout = () => { localStorage.removeItem("halgaros_sa_token"); localStorage.removeItem("halgaros_sa_user"); setSaUser(null); setToken(""); };

  if (token) return <Dashboard token={token} onLogout={logout} perms={saUser?.valhalla_perms ?? null} />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#09090b] p-4">
      <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 p-8">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/halgaros-icon-128.png" alt="HalgarOS" className="h-14 w-14 rounded-2xl shadow-[0_0_24px_rgba(255,106,0,0.4)]" />
          <span className="mt-1 font-display text-3xl font-bold tracking-tight text-white">Valhalla</span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">Back-office HalgarOS</span>
        </div>
        {challenge ? (
          <div className="space-y-3" data-testid="sa-2fa-login">
            <p className="text-sm text-zinc-400">Saisissez le code à 6 chiffres de votre application d'authentification (ou un code de secours).</p>
            <div><Label className="text-zinc-400">Code de vérification</Label><Input data-testid="sa-2fa-login-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()} className="mt-1 border-zinc-800 bg-zinc-900 tracking-widest text-white" placeholder="123456" /></div>
            <Button data-testid="sa-2fa-login-verify" onClick={verify} disabled={busy} className="w-full rounded-full bg-[#FF6A00] font-semibold hover:bg-[#E65F00]">{busy ? "…" : "Vérifier"}</Button>
            <button data-testid="sa-2fa-login-cancel" onClick={() => { setChallenge(null); setCode(""); }} className="w-full text-center text-sm text-zinc-500 hover:text-white">Retour</button>
          </div>
        ) : (
        <div className="space-y-3">
          <div><Label className="text-zinc-400">Email</Label><Input data-testid="sa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          <div><Label className="text-zinc-400">Mot de passe</Label><Input data-testid="sa-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          <Button data-testid="sa-login" onClick={login} disabled={busy} className="w-full rounded-full bg-[#FF6A00] font-semibold hover:bg-[#E65F00]">{busy ? "Connexion…" : "Se connecter"}</Button>
        </div>
        )}
      </div>
    </div>
  );
}
